import { connect } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import { useIntl } from "react-intl";
import List from "../../../common/list";
import { TextInput } from "../../../common/fields";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import {
  listRequest,
  setQuery,
} from "../../../../store/slices/learningMilestoneSlice";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { AccessScopeConstants, RouteConstants } from "../../../../constants";
import { useNavigate } from "react-router-dom";

const LearningMilestoneList = ({
  action,
  labels,
  list,
  listRequest,
  query,
  requesting,
  setQuery,
  user,
  totalCount,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [namePattern, setNamePattern] = useState("");
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [createdByNamePattern, setCreatedByNamePattern] = useState("");

  const { role } = user || {};

  const canAdd = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.CreateScope
  );

  const fetchList = useCallback(() => {
    listRequest({
      ...query,
      sortColumn: query.sortColumn || "name",
    });
  }, [query, listRequest]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query?.namePattern !== namePattern) {
        setQuery({ ...query, namePattern, pageNo: 1 });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [namePattern, setQuery, query]);

  const onSelectionChange = (selectedRecords) => {
    setSelectedRecords(selectedRecords?.value || []);
  };

  const handlePageChange = (pageData) => {
    if (
      query.pageSize !== pageData.rows ||
      query.pageNo !== pageData.page + 1
    ) {
      setQuery({
        ...query,
        pageNo: pageData.page + 1,
        pageSize: pageData.rows,
      });
    }
  };

  const handleOnSortChange = (sortData) => {
    setQuery({
      ...query,
      pageNo: 1,
      sortColumn: sortData.sortField,
      sortBy: sortData.sortOrder === 1 ? "asc" : "desc",
    });
  };

  const handleAddLearningObject = () => {
    navigate(RouteConstants.APP_ROUTE_CONTENTS_LEARNING_MILESTONES_ADD);
  };

  const onClone = (id) => {
    navigate(
      `${RouteConstants.APP_ROUTE_CONTENTS_LEARNING_MILESTONES_ADD}?clone=${id}`
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query?.createdBy !== createdByNamePattern) {
        setQuery({ ...query, createdBy: createdByNamePattern, pageNo: 1 });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [createdByNamePattern, setQuery, query]);

  const otherActions = [
    {
      label: "clone",
      icon: "pi pi-clone",
      isAllowed: canAdd,
      action: (data) => onClone(data.id),
    },
  ];

  return (
    <div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "learning_milestones" })}
          </div>
          <div className="edexlms-page-actions">
            <Button
              icon="pi pi-plus"
              onClick={handleAddLearningObject}
              size="small"
              label={intl.formatMessage({ id: "add_learning_milestone" })}
              type="button"
              disabled={!canAdd}
              tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
              tooltip={
                canAdd ? "" : intl.formatMessage({ id: "no_permission" })
              }
            />
          </div>
        </div>
        <div className="edexlms-page-body">
          <div className="edexlms-search-filter-bar">
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({
                id: "search_learning_milestones",
              })}
              input={{
                name: "search",
                value: namePattern,
                onChange: (e) => setNamePattern(e.target.value),
              }}
            />
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({
                id: "search_created_by",
              })}
              input={{
                name: "search",
                value: createdByNamePattern,
                onChange: (e) => setCreatedByNamePattern(e.target.value),
              }}
            />
          </div>
          <List
            totalRecords={totalCount}
            loading={action === "list" && requesting}
            onSelectionChange={onSelectionChange}
            handlePageChange={handlePageChange}
            handleOnSortChange={handleOnSortChange}
            data={list || []}
            selectedRecords={selectedRecords}
            pageQuery={query}
            handleRowClick={(data) =>
              navigate(
                `${RouteConstants.APP_ROUTE_CONTENTS_LEARNING_MILESTONES_EDIT.replace(
                  ":id",
                  data.data.id
                )}`
              )
            }
            selectionMode=""
            columns={[
              {
                field: "name",
                header: "name",
                sortable: true,
              },
              // {
              //   field: "tags",
              //   header: "tags",
              //   sortable: false,
              //   body: ListCellRenders.Tags,
              // },
              {
                field: "isLinear",
                header: "is_linear",
                sortable: false,
                body: ListCellRenders.BooleanText("isLinear"),
              },
              // {
              //   field: "isCertified",
              //   header: "isCertified",
              //   sortable: false,
              //   body: ListCellRenders.BooleanText("isCertified"),
              // },
              // {
              //   field: "rewardId",
              //   header: "reward",
              //   sortable: false,
              //   body: ListCellRenders.BooleanText("rewardId"),
              // },
              {
                field: "createdByUser",
                header: "created_by",
                sortable: false,
                body: ListCellRenders.CreatedBy,
              },
              {
                field: "createdAt",
                header: "created_at",
                sortable: true,
                body: ListCellRenders.DateTime("createdAt"),
              },
              {
                field: "updatedAt",
                header: "updated_at",
                sortable: true,
                body: ListCellRenders.DateTime("updatedAt"),
              },
              {
                field: "actions",
                headerIcon: "pi-cog",
                align: "center",
                body: ListCellRenders.OtherActions(otherActions),
              },
              // {
              //   field: "actions",
              //   headerIcon: "pi-cog",
              //   align: "center",
              //   body: ListCellRenders.OtherActions([
              //     {
              //       label: "edit",
              //       icon: "pi-pencil",
              //       isAllowed: true,
              //       action: (data) =>
              //         navigate(
              //           `${RouteConstants.APP_ROUTE_CONTENTS_LEARNING_MILESTONES_EDIT.replace(
              //             ":id",
              //             data.id
              //           )}`
              //         ),
              //     },
              //   ]),
              // },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  list: state?.learningMilestones?.data?.list || [],
  action: state?.learningMilestones?.action,
  requesting: state?.learningMilestones?.requesting,
  error: state?.learningMilestones?.error,
  query: state?.learningMilestones?.query,
  user: state?.self?.user,
  totalCount: state?.learningMilestones?.data?.totalCount,
});

const mapDispatchToProps = (dispatch) => ({
  listRequest: (data) => dispatch(listRequest(data)),
  setQuery: (data) => dispatch(setQuery(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LearningMilestoneList);
